/** @jsxImportSource @emotion/react */
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import * as S from "./MultiSelectBase.styles";
import * as SS from "./SingleSelectFilter.styles";
import { useMultiSelectPanel } from "./useMultiSelectPanel";
import { getResource } from "../../language/resources/getResource";
import { ItemKey, Accessor, readWithAccessor } from "./types";
import { css, SerializedStyles } from "@emotion/react";
import filterIcon from "../../assets/icons/filter.svg";

const iconButtonCss = css`
  cursor: pointer;
  pointer-events: auto;
`;

export type SingleSelectFilterProps<T> = {
  resources: Record<string, any>;
  items: T[];

  keyAccessor: Accessor<T, ItemKey>;
  labelAccessor: Accessor<T, string>;

  selectedItem?: T;
  onChange: (item: T) => void;

  /** Shown in the control while nothing is selected. */
  placeholder: string;
  /** Shown in the panel when there is nothing to choose from. */
  noItemsMessage?: string;

  disabled?: boolean;

  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Render the search field in the control instead of at the top of the panel. */
  searchInControl?: boolean;

  requiredAsterisk?: boolean;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  rootCss?: SerializedStyles;
  buttonCss?: SerializedStyles;
  /** Style overrides for the dropdown panel. */
  panelCss?: SerializedStyles;
  placeholderTextCss?: SerializedStyles;
  selectedTextCss?: SerializedStyles;
  iconCss?: SerializedStyles;
  dropdownIconSrc?: string;
};

/**
 * A single-choice control drawn with the same floating panel as MultiSelectFilter. Use instead of
 * a native <select> where the list needs styling the browser will not allow.
 */
export function SingleSelectFilter<T>({
  resources,
  items,
  keyAccessor,
  labelAccessor,

  selectedItem,
  onChange,

  placeholder,
  noItemsMessage,

  disabled = false,

  showSearch = false,
  searchPlaceholder = "Search…",
  searchInControl = false,

  requiredAsterisk = false,

  controlWidth,
  controlMaxWidth,
  controlHeight,
  iconSize = 24,

  panelMaxWidth,
  panelMaxHeight = 240,

  rootCss,
  buttonCss,
  panelCss,
  placeholderTextCss,
  selectedTextCss,
  iconCss,
  dropdownIconSrc,
}: SingleSelectFilterProps<T>) {
  const { open, setOpen, btnRef, panelRef, panelMinWidth } =
    useMultiSelectPanel();

  const [search, setSearch] = useState("");

  /* Virtual focus: real focus stays on the trigger, or on the search field so that typing
     survives. The highlighted option is published via aria-activedescendant. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reopening starts from the full list.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveIndex(-1);
    }
  }, [open]);
  const popupId = useId();

  const normalized = useMemo(
    () =>
      items.map((item) => ({
        key: readWithAccessor(item, keyAccessor),
        label: readWithAccessor(item, labelAccessor),
        raw: item,
      })),
    [items, keyAccessor, labelAccessor],
  );

  const filtered = useMemo(() => {
    if (!showSearch || !search.trim()) return normalized;
    const q = search.toLowerCase();
    return normalized.filter((i) => i.label.toLowerCase().includes(q));
  }, [normalized, search, showSearch]);

  /* Typing can shrink the list out from under the highlight. */
  useEffect(() => {
    setActiveIndex((i) => (i < filtered.length ? i : filtered.length - 1));
  }, [filtered.length]);

  const selectedKey =
    selectedItem == null
      ? undefined
      : readWithAccessor(selectedItem, keyAccessor);

  const noneSelected = selectedItem == null;

  const buttonText = noneSelected
    ? placeholder
    : readWithAccessor(selectedItem!, labelAccessor);

  const searching = open && showSearch && searchInControl;

  // Outside clicks must not refocus the trigger: that would pull focus off whatever was clicked.
  const restoreFocusOnClose = useRef(false);

  const closeAndRefocus = () => {
    restoreFocusOnClose.current = true;
    setOpen(false);
  };

  useEffect(() => {
    if (open || !restoreFocusOnClose.current) return;
    restoreFocusOnClose.current = false;
    btnRef.current?.focus();
  }, [open, btnRef]);

  const choose = (item: T) => {
    onChange(item);
    closeAndRefocus();
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    /* Called optionally: jsdom has no scrollIntoView. */
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  /* The one lit row: wherever the mouse or the arrows last landed, else the selection. */
  const highlightedIndex =
    activeIndex >= 0
      ? activeIndex
      : filtered.findIndex((o) => o.key === selectedKey);

  const moveActive = (delta: number) => {
    if (filtered.length === 0) return;

    /* Arrows step off whatever is lit; with nothing lit, enter from the end the key points at. */
    if (highlightedIndex < 0) {
      setActiveIndex(delta > 0 ? 0 : filtered.length - 1);
      return;
    }

    setActiveIndex(
      Math.max(0, Math.min(filtered.length - 1, highlightedIndex + delta)),
    );
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "Escape":
        if (!open) return;
        e.stopPropagation();
        closeAndRefocus();
        return;

      case "ArrowDown":
      case "ArrowUp":
        /* The list scrolls, not the page. */
        e.preventDefault();
        if (open) moveActive(e.key === "ArrowDown" ? 1 : -1);
        else setOpen(true);
        return;

      case "Home":
      case "End":
        if (!open || filtered.length === 0) return;
        e.preventDefault();
        setActiveIndex(e.key === "Home" ? 0 : filtered.length - 1);
        return;

      case "Enter":
        if (!open || activeIndex < 0) return;
        /* Or the trigger's own click activation reopens the panel. */
        e.preventDefault();
        choose(filtered[activeIndex].raw);
        return;

      default:
        return;
    }
  };

  return (
    <S.Root
      onKeyDown={onKeyDown}
      css={css`
        ${rootCss}
      `}
    >
      <S.Button
        /* A <button> may not contain the search <input>. */
        as={searching ? "div" : undefined}
        ref={btnRef as any}
        css={buttonCss}
        /* preventDefault: the wrapping <label> forwards a second click that reopens the panel. */
        onClick={(e) => {
          e.preventDefault();
          if (disabled) return;
          /* Anywhere but the text field closes -- the chevron included. */
          if (searching) closeAndRefocus();
          else setOpen((o) => !o);
        }}
        /* is-open: while open the trigger is a div, so :focus cannot match it. */
        className={
          [disabled ? "is-disabled" : "", open ? "is-open" : ""]
            .filter(Boolean)
            .join(" ") || undefined
        }
        controlWidth={controlWidth}
        controlMaxWidth={controlMaxWidth}
        controlHeight={controlHeight}
        iconSize={iconSize}
        title={searching ? undefined : buttonText}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${popupId}-${activeIndex}` : undefined
        }
      >
        <S.ButtonBody>
          {searching ? (
            <S.ControlSearch onClick={(e) => e.stopPropagation()}>
              <S.ControlSearchInput
                autoFocus
                css={selectedTextCss}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
              {search && (
                <S.ClearButton
                  type="button"
                  onClick={() => setSearch("")}
                  title={getResource(resources, "ACCESS_OVERVIEW_CLEAR_SEARCH")}
                >
                  ×
                </S.ClearButton>
              )}
            </S.ControlSearch>
          ) : (
            <S.ButtonText
              isPlaceholder={noneSelected}
              css={noneSelected ? placeholderTextCss : selectedTextCss}
            >
              {buttonText}
              {noneSelected && requiredAsterisk && <S.RequiredMarker />}
            </S.ButtonText>
          )}
        </S.ButtonBody>

        <S.FilterIcon
          iconSize={iconSize}
          /* stopPropagation keeps the container's handler from undoing this. */
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;
            if (open) closeAndRefocus();
            else setOpen(true);
          }}
          css={iconButtonCss}
        >
          <img
            src={dropdownIconSrc ?? filterIcon}
            alt=""
            draggable={false}
            width={iconSize}
            height={iconSize}
            css={noneSelected ? iconCss : undefined}
          />
        </S.FilterIcon>
      </S.Button>

      {open && (
        <SS.Panel
          css={panelCss}
          ref={panelRef}
          minW={panelMinWidth}
          maxW={panelMaxWidth}
        >
          {showSearch && !searchInControl && (
            <>
              <S.Search css={SS.InsetRow}>
                <S.SearchFieldWrapper>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                  />
                  {search && (
                    <S.ClearButton
                      type="button"
                      onClick={() => setSearch("")}
                      title={getResource(
                        resources,
                        "ACCESS_OVERVIEW_CLEAR_SEARCH",
                      )}
                    >
                      ×
                    </S.ClearButton>
                  )}
                </S.SearchFieldWrapper>
              </S.Search>

              <S.Divider />
            </>
          )}

          <S.OptionsScroll
            id={popupId}
            role="listbox"
            /* Leaving the list returns the highlight to the selection. */
            onMouseLeave={() => setActiveIndex(-1)}
            panelMaxHeight={panelMaxHeight}
          >
            {filtered.map((i, index) => (
              <SS.Choice
                key={String(i.key)}
                id={`${popupId}-${index}`}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                type="button"
                role="option"
                /* One tab stop for the whole control; the arrow keys do the rest. */
                tabIndex={-1}
                className={index === highlightedIndex ? "is-active" : undefined}
                /* One highlight for both inputs; arrowing carries on from where the mouse
                   left it. */
                onMouseEnter={() => setActiveIndex(index)}
                selected={i.key === selectedKey}
                aria-selected={i.key === selectedKey}
                onClick={() => choose(i.raw)}
              >
                {i.label}
              </SS.Choice>
            ))}

            {filtered.length === 0 && (
              <SS.NoItems>
                {items.length === 0 && noItemsMessage
                  ? noItemsMessage
                  : getResource(resources, "ACCESS_OVERVIEW_NO_OPTIONS")}
              </SS.NoItems>
            )}
          </S.OptionsScroll>
        </SS.Panel>
      )}
    </S.Root>
  );
}
